<template>
  <!-- Primary links -->
  <BNavItem
    v-if="!approvedContributor && !showDeveloperProfile"
    to="/become-a-developer"
    @click="emitNavigate"
  >
    <PhScroll class="naviIcon me-2" weight="fill" />
    <span>Become a developer</span>
  </BNavItem>

  <BNavItem
    v-if="showDeveloperProfile && !approvedContributor"
    :to="`/users/${authUserUuid}/developer/profile`"
    @click="emitNavigate"
  >
    <PhFileCode class="naviIcon me-2" weight="fill" />
    <span class="mr-4">Developer Profile</span>
  </BNavItem>

  <BNavItem
    v-if="approvedContributor"
    :to="`/users/${authUserUuid}/developer/software-products`"
    @click="emitNavigate"
  >
    <PhCode class="naviIcon me-2" weight="fill" />
    <span class="mr-4">Software Products</span>
  </BNavItem>

  <BNavItem v-if="loggedIn" to="/devices" @click="emitNavigate">
    <PhCpu class="naviIcon me-2" weight="fill" />
    <span>Devices</span>
  </BNavItem>

  <BNavItem v-if="loggedIn" to="/licenses" @click="emitNavigate">
    <PhScroll class="naviIcon me-2" weight="fill" />
    <span>Licenses</span>
  </BNavItem>

  <BNavItem to="/support" @click="emitNavigate">
    <PhHeadset class="naviIcon me-2" weight="fill" />
    <span>Support</span>
  </BNavItem>

  <!-- User menu: dropdown on desktop, flat list on mobile -->
  <template v-if="loggedIn && variant === 'desktop'">
    <BNavItemDropdown>
      <template #button-content>
        <PhUser class="naviIcon me-2" />
        <span data-testid="header-user">{{ authUserName }}</span>
      </template>

      <BDropdownItem
        :active="isProfileTabActive('')"
        :class="[
          'navi__drop__item navi__drop__item--lowercase',
          userRegistrationStatusIncludesUserData ? 'profile-warning' : '',
        ]"
        :to="`/users/${authUserUuid}/profile`"
        @click="emitNavigate"
      >
        <PhUser class="naviIcon me-2" />User Profile
        <PhWarning
          v-if="userRegistrationStatusIncludesUserData"
          class="ms-2"
          weight="fill"
        />
      </BDropdownItem>

      <BDropdownItem
        v-if="authUserCompanyId"
        :active="isProfileTabActive('')"
        :class="[
          'navi__drop__item navi__drop__item--lowercase',
          userRegistrationStatusIncludesCompanyData ? 'profile-warning' : '',
        ]"
        :to="`/users/${authUserUuid}/company/${authUserCompanyId}/profile`"
        @click="emitNavigate"
      >
        <PhFactory class="naviIcon me-2" weight="fill" />Company
        <PhWarning
          v-if="userRegistrationStatusIncludesCompanyData"
          class="ms-2"
          weight="fill"
        />
      </BDropdownItem>

      <BDropdownItem
        v-if="authUserIsAdmin"
        active-class="navi__drop__item--active"
        class="navi__drop__item navi__drop__item--lowercase"
        to="/admin/dashboard"
        @click="emitNavigate"
      >
        <PhUserCircleGear class="naviIcon me-2" weight="fill" />Admin
      </BDropdownItem>

      <BDropdownItem
        active-class="navi__drop__item--active"
        class="navi__drop__item navi__drop__item--lowercase"
        @click="
          emitNavigate();
          logout();
        "
      >
        <PhSignOut class="naviIcon me-2" weight="fill" />Logout
      </BDropdownItem>
    </BNavItemDropdown>
  </template>

  <template v-else-if="loggedIn && variant === 'mobile'">
    <BNavItem
      :to="`/users/${authUserUuid}/profile`"
      class="mobileUserItem"
      @click="emitNavigate"
    >
      <PhUser class="naviIcon me-2" weight="regular" />User Profile
      <PhWarning
        v-if="userRegistrationStatusIncludesUserData"
        weight="fill"
        class="ms-2"
      />
    </BNavItem>

    <BNavItem
      v-if="authUserCompanyId"
      :to="`/users/${authUserUuid}/company/${authUserCompanyId}/profile`"
      class="mobileUserItem"
      @click="emitNavigate"
    >
      <PhFactory class="naviIcon me-2" weight="fill" />Company
      <PhWarning
        v-if="userRegistrationStatusIncludesCompanyData"
        weight="fill"
        class="ms-2"
      />
    </BNavItem>

    <BNavItem
      v-if="authUserIsAdmin"
      to="/admin"
      class="mobileUserItem"
      @click="emitNavigate"
    >
      <PhUserCircleGear class="naviIcon me-2" weight="fill" />Admin
    </BNavItem>

    <BNavItem
      class="mobileUserItem"
      @click="
        emitNavigate();
        logout();
      "
    >
      <PhSignOut class="naviIcon me-2" weight="fill" />Logout
    </BNavItem>
  </template>

  <!-- Login -->
  <BNavItem
    v-if="!loggedIn"
    @click="
      emitNavigate();
      openLoginModal();
    "
  >
    <PhSignIn class="naviIcon me-2" /><span>Login / Register</span>
  </BNavItem>
</template>

<script setup lang="ts">
import { BNavItem, BNavItemDropdown, BDropdownItem } from "bootstrap-vue-next";
import {
  PhCpu,
  PhHeadset,
  PhScroll,
  PhCode,
  PhWarning,
  PhUserCircleGear,
  PhSignOut,
  PhFactory,
  PhUser,
  PhFileCode,
  PhSignIn,
} from "@phosphor-icons/vue";

defineProps<{
  variant: "desktop" | "mobile";

  loggedIn: boolean;
  approvedContributor: boolean;
  showDeveloperProfile: boolean;

  authUserUuid?: string;
  authUserName?: string;
  authUserCompanyId?: string;

  authUserIsAdmin: boolean;

  userRegistrationStatusIncludesUserData: boolean;
  userRegistrationStatusIncludesCompanyData: boolean;

  isProfileTabActive: (tabName: string) => boolean;
  openLoginModal: () => void;
  logout: () => void;
}>();

const emit = defineEmits<{
  (e: "navigate"): void;
}>();

function emitNavigate(): void {
  emit("navigate");
}
</script>

<!-- cannot be scoped -->
<style lang="scss">
@use "@/sass/variables.scss" as *;
#navigation .nav-link,
#profile_tabs .nav-link {
  color: $gray-100;
  &:hover,
  &:focus,
  &.active {
    color: $gray-100;
  }
}
</style>
<style scoped lang="scss">
@use "@/sass/components/HeaderNavItems.scss" as *;
</style>
